import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Checkbox, Alert, Stack, LinearProgress, Box, Collapse, Link, SvgIcon } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../services/api';

const CareerApplyPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);
  const [phone, setPhone] = React.useState('+48 ');
  const [phoneError, setPhoneError] = React.useState(false);
  const [cvFile, setCvFile] = React.useState<File | null>(null);
  const [coverFile, setCoverFile] = React.useState<File | null>(null);
  const [jobOffersConsent, setJobOffersConsent] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
  const [showRodoDetails, setShowRodoDetails] = React.useState(false);
  const [showJobOffersDetails, setShowJobOffersDetails] = React.useState(false);
  const siteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY as string) || '';
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [offerTitle, setOfferTitle] = React.useState<string>('');
  const [offerWorkPlace, setOfferWorkPlace] = React.useState<string>('');
  const grey600 = '#5F6368';

  const UncheckedIcon = (props: any) => (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ width: 24, height: 24 }}>
      <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2Z" />
    </SvgIcon>
  );
  const CheckedIcon = (props: any) => (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ width: 24, height: 24 }}>
      {/* Filled rounded square */}
      <rect x="3" y="3" width="18" height="18" rx="4" ry="4" fill={grey600} />
      {/* White check mark */}
      <path d="M10 17 5 12.5l1.41-1.41L10 14l7.59-7.59L19 7 10 17Z" fill="#FFFFFF" />
    </SvgIcon>
  );

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!slug) return;
        const resp = await api.get(`/recruit/public/offers/${slug}`);
        if (!mounted) return;
        const d = resp?.data?.data || {};
        setOfferTitle(d?.title_pl || d?.title_en || '');
        setOfferWorkPlace(d?.work_place || '');
      } catch {
        // leave empty silently
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value || '';
    // allow only "+" and digits in the model, formatting added below
    let normalized = raw.replace(/[^\d+]/g, '');

    if (normalized.startsWith('48') && !normalized.startsWith('+')) {
      normalized = '+' + normalized;
    }

    let formatted = normalized;
    if (normalized.startsWith('+48')) {
      // Polish format: +48 605 603 530
      let after = normalized.replace(/^\+48/, '');
      after = after.replace(/\D/g, '').slice(0, 9); // cap to 9 digits after +48
      const g1 = after.slice(0, 3);
      const g2 = after.slice(3, 6);
      const g3 = after.slice(6, 9);
      formatted = '+48' + (g1 ? ' ' + g1 : '') + (g2 ? ' ' + g2 : '') + (g3 ? ' ' + g3 : '');
    } else {
      // Other country codes: keep as typed (digits and optional leading plus)
      if (!normalized.startsWith('+') && normalized.length > 0) {
        formatted = '+' + normalized;
      } else {
        formatted = normalized;
      }
    }

    setPhone(formatted);

    // validation
    if (formatted.startsWith('+48')) {
      const digitsAfter = formatted.replace(/^\+48\s?/, '').replace(/\D/g, '');
      setPhoneError(digitsAfter.length > 0 && digitsAfter.length !== 9);
    } else {
      setPhoneError(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';
    setEmail(value);
    const emailRegex = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
    setEmailError(value.length > 0 && !emailRegex.test(value));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setOk(false);
    try {
      if (!slug) throw new Error('Brak oferty');
      if (!cvFile) throw new Error('Dodaj plik CV');
      if (cvFile.size > 10 * 1024 * 1024) throw new Error('Maksymalny rozmiar CV to 10 MB');
      if (!siteKey) throw new Error('Brak konfiguracji reCAPTCHA');
      // Phone validation (optional field)
      const trimmedPhone = (phone || '').trim();
      if (trimmedPhone.startsWith('+48')) {
        const digitsAfter = trimmedPhone.replace(/^\+48\s?/, '').replace(/\D/g, '');
        if (digitsAfter.length !== 9) {
          throw new Error('Dla kierunkowego +48 wymagane jest 9 cyfr.');
        }
      }
      // Email validation
      const emailRegex = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Nieprawidłowy adres e‑mail');
      }
      if (!captchaToken) throw new Error('Zaznacz reCAPTCHA');

      setSubmitting(true);

      const form = new FormData();
      form.append('first_name', firstName);
      form.append('last_name', lastName);
      form.append('email', email);
      form.append('phone', phone);
      form.append('job_offer_slug', slug);
      form.append('rodo_consent', '1');
      form.append('job_offers_consent', jobOffersConsent ? '1' : '0');
      form.append('captcha_token', captchaToken);
      form.append('cv_file', cvFile);
      if (coverFile) form.append('cover_letter_file', coverFile);

      const resp = await api.post('/recruit/public/applications/submit', form);
      if (resp.data?.success) {
        setOk(true);
      } else {
        throw new Error(resp.data?.message || 'Nie udało się wysłać zgłoszenia');
      }
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Wystąpił błąd');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6, bgcolor: '#FAFAFA' }}>
      {/* Logo */}
      <Box sx={{ display:'flex', justifyContent:'center' }}>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
            fontWeight: 600,
            fontSize: '21.94px',
            lineHeight: '26px',
            letterSpacing: '0.12em',
            color: 'rgba(197, 48, 48, 1)',
            width: '140px',
            height: '26px',
            display: 'inline-block',
            textAlign: 'center'
          }}
        >
          UNITRESCO
        </Typography>
      </Box>
      {/* Tytuł */}
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
          fontWeight: 600,
          fontSize: '48px',
          lineHeight: '100%',
          letterSpacing: 0,
          color: 'rgba(2, 2, 66, 1)',
          mt: '60px',
          mb: 1,
          textAlign: 'center'
        }}
      >
        Formularz zgłoszeniowy
      </Typography>
      {(offerTitle || offerWorkPlace) && (
        <Box sx={{ textAlign: 'center', mb: '24px' }}>
          {!!offerTitle && (
            <Typography sx={{ fontFamily:'Roboto', fontWeight:700, fontSize:'18px', color:'rgba(2, 2, 66, 1)' }}>{offerTitle}</Typography>
          )}
          {!!offerWorkPlace && (
            <Typography variant="body2" color="text.secondary">{offerWorkPlace}</Typography>
          )}
        </Box>
      )}
      {/* Widok po wysłaniu */}
      {ok ? (
        <Box sx={{ width: 920, maxWidth:'100%', mx: 'auto', p: 3, border: '1px solid rgba(2, 2, 66, 0.12)', borderRadius: '8px', bgcolor: '#fff' }}>
          <Box sx={{ width: 792, height: 432, mx: 'auto', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: 6 }}>
            <Box sx={{ width: 120, height: 120, borderRadius: '100px', bgcolor: 'rgba(224, 255, 238, 1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <MarkEmailReadOutlinedIcon sx={{ fontSize: 40, color: '#020242' }} />
            </Box>
            <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap: 2 }}>
              <Typography sx={{ fontWeight: 700, color: 'rgba(2, 2, 66, 1)' }}>Twoja aplikacja została przyjęta.</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(2, 2, 66, 1)', textAlign:'center' }}>Na wskazany adres email wysłaliśmy link do testu (Etap 1).</Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Wrapper formularza */
        <Box sx={{ width: 792, mx: 'auto', bgcolor: '#fff', borderRadius: '4px', p: '60px 80px', boxShadow: '0 0 0 1px rgba(2, 2, 66, 0.14)' }}>
        <form onSubmit={onSubmit}>
          <Stack spacing={4}>
            {err && <Alert severity="error">{err}</Alert>}
            {/* Lead */}
            <Typography sx={{
              fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '150%',
              color: 'rgba(89, 89, 102, 1)'
            }}>
              Wypełnij obowiązkowe pola i załącz swoje CV
            </Typography>
            {/* Pierwszy wiersz: Imię / Nazwisko */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Imię" value={firstName} onChange={(e)=>setFirstName(e.target.value)} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'rgba(197, 48, 48, 1)' } } }} />
              <TextField fullWidth label="Nazwisko" value={lastName} onChange={(e)=>setLastName(e.target.value)} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'rgba(197, 48, 48, 1)' } } }} />
            </Box>
            <TextField fullWidth type="email" label="Adres e‑mail" value={email} onChange={handleEmailChange} error={emailError} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'rgba(197, 48, 48, 1)' } } }} helperText="" />
            <TextField fullWidth label="Telefon" placeholder="+48 605 603 530" value={phone} onChange={handlePhoneChange} error={phoneError} helperText="" inputProps={{ inputMode: 'tel' }} />
            {/* Załącz CV */}
            {!cvFile ? (
              <Box sx={{ display:'flex', alignItems:'center', gap: 2 }}>
                <Button variant="contained" component="label" sx={{
                  alignSelf:'flex-start',
                  textTransform: 'none',
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 500,
                  fontSize: '14px',
                  borderRadius: '4px',
                  padding: '10px 16px',
                  color: 'rgba(255, 255, 255, 1)',
                  backgroundColor: 'rgba(2, 2, 66, 1)',
                  '&:hover': { backgroundColor: 'rgba(2, 2, 66, 0.78)' }
                }}>Załącz CV<input ref={fileInputRef} hidden type="file" accept="application/pdf,.pdf,.doc,.docx" onChange={(e)=>{
                  const files = Array.from(e.target.files || []);
                  setCvFile(files[0] || null);
                  setErr(null); setOk(false);
                }} /></Button>
                <Typography variant="body2" color="text.secondary">Nie wybrano pliku CV</Typography>
              </Box>
            ) : (
              <Box sx={{
                display:'flex', alignItems:'center', gap: 2,
                border: '1px solid rgba(2, 2, 66, 1)',
                borderRadius: '4px',
                px: 2, py: 1.5,
                transition: 'box-shadow .15s ease',
                '&:hover': { boxShadow: '0 0 0 1px rgba(2, 2, 66, 0.6)' }
              }}>
                <Box sx={{ width: 56, height: 56, borderRadius: '4px', bgcolor: '#e0e0e0', flex: '0 0 auto' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500, color: 'rgba(2, 2, 66, 1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cvFile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{Math.max(1, Math.round(cvFile.size/1024))}kb</Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Button type="button" onClick={()=>{ setCvFile(null); try{ if(fileInputRef.current) fileInputRef.current.value=''; }catch{} }} sx={{ textTransform:'none', color: 'rgba(197, 48, 48, 1)', minWidth: 0, p: 0 }}>Usuń</Button>
                </Box>
              </Box>
            )}
            <Box>
              <Box sx={{ ml: 5 }}>
                <Box sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  Klikając w przycisk „Aplikuj” lub w inny sposób wysyłając zgłoszenie rekrutacyjne, zgadzasz się na przetwarzanie Twoich danych osobowych przez UNITRESCO sp. z o.o. z siedzibą w Białymstoku, jako administratora danych osobowych w celu przeprowadzenia rekrutacji na stanowisko wskazane w ogłoszeniu. Pełną informację odnośnie przetwarzania Twoich danych osobowych.
                </Box>
              </Box>
              <Box sx={{ ml: 5, mt: 1 }}>
                <Button size="small" variant="text" onClick={()=>setShowRodoDetails(v=>!v)} endIcon={showRodoDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ px: 0, minWidth: 0, fontSize: 12.5, color: grey600, textTransform: 'none', ml: '10px' }}>
                  {showRodoDetails ? 'ukryj' : 'rozwiń'}
                </Button>
                <Collapse in={showRodoDetails} timeout="auto" unmountOnExit>
                  <Box sx={{ fontSize: 12.5, lineHeight: 1.5, color: 'text.primary', mt: 1 }}>
                    <ol style={{ marginTop: 6, marginBottom: 0, paddingLeft: 16 }}>
                      <li><b>ADMINISTRATOR DANYCH</b><br/>UNITRESCO sp. z o.o., ul. Żurawia 71, 15-540 Białystok</li>
                      <li><b>KONTAKT</b><br/>E-mail: biuro@unitresco.com</li>
                      <li><b>CEL I PODSTAWA PRZETWARZANIA</b><br/>
                        Dane przetwarzamy w celu:<br/>
                        Przeprowadzenia obecnego procesu rekrutacji - na podstawie art. 6 ust. 1 lit. b RODO (podjęcie działań przed zawarciem umowy) oraz art. 6 ust. 1 lit. c RODO w zakresie danych wymaganych przepisami prawa pracy, a w przypadku podania w CV i przekazanych dokumentach danych niewymaganych przepisami prawa na podstawie zgody, za jaką zostanie potraktowane ich przekazanie (art. 6 ust. 1 lit. a RODO).<br/>
                        Przyszłych rekrutacji - na podstawie art. 6 ust. 1 lit. a RODO (zgoda)
                      </li>
                      <li><b>ZAKRES DANYCH</b><br/>
                        Dane wymagane przepisami: imię, nazwisko, data urodzenia, dane kontaktowe, wykształcenie, przebieg dotychczasowego zatrudnienia<br/>
                        Inne dane: podane dobrowolnie w CV i przekazanych dokumentach
                      </li>
                      <li><b>ODBIORCY DANYCH</b><br/>
                        Dane mogą być przekazane dostawcom usługi publikacji ogłoszeń o pracę, dostawcom systemów do zarządzania rekrutacjami, dostawcom usług IT (hosting), dostawcom systemów informatycznych. 
                      </li>
                      <li><b>OKRES PRZECHOWYWANIA</b><br/>
                        Obecna rekrutacja: do 4 miesięcy od zakończenia procesu rekrutacji<br/>
                        Przyszłe rekrutacje: 36 miesięcy od wyrażenia zgody
                      </li>
                      <li><b>PRAWA KANDYDATA</b><br/>
                        Przysługuje Pani/Panu prawo do:<br/>
                        Dostępu do danych<br/>
                        Sprostowania danych<br/>
                        Usunięcia danych<br/>
                        Ograniczenia przetwarzania<br/>
                        Sprzeciwu<br/>
                        Cofnięcia zgody<br/>
                        Wniesienia skargi do Prezesa UODO
                      </li>
                      <li><b>INFORMACJA O DOBROWOLNOŚCI</b><br/>
                        Podanie danych w zakresie wynikającym z Kodeksu pracy jest wymogiem ustawowym. Podanie pozostałych danych jest dobrowolne.
                      </li>
                    </ol>
                  </Box>
                </Collapse>
              </Box>
            </Box>
            {/* Zgoda na kontakt dot. ofert pracy (3 lata) */}
            <Box>
              <Box sx={{ ml: 5, display:'grid', gridTemplateColumns: '28px 1fr', columnGap: 1, alignItems:'flex-start' }}>
                <Box>
                  <Checkbox
                    checked={jobOffersConsent}
                    onChange={(e)=>setJobOffersConsent(e.target.checked)}
                    sx={{ mt: '-5px', color: grey600, '&.Mui-checked': { color: grey600 } }}
                    icon={<UncheckedIcon htmlColor={grey600} />}
                    checkedIcon={<CheckedIcon htmlColor={grey600} />}
                  />
                </Box>
                <Box sx={{ fontSize: 13, lineHeight: 1.5 }}><b>ZGODA NA PRZYSZŁE REKRUTACJE (opcjonalnie)</b><br/>Wyrażam zgodę na przetwarzanie moich danych osobowych przez UNITRESCO sp. z o.o. również na potrzeby przyszłych rekrutacji prowadzonych przez okres najbliższych 3 lat.</Box>
              </Box>
              <Box sx={{ ml: 5, mt: 1 }}>
                <Button size="small" variant="text" onClick={()=>setShowJobOffersDetails(v=>!v)} endIcon={showJobOffersDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ px: 0, minWidth: 0, fontSize: 12.5, color: grey600, textTransform: 'none', ml: '10px' }}>
                  {showJobOffersDetails ? 'Ukryj treść oświadczenia' : 'Pokaż treść oświadczenia'}
                </Button>
                <Collapse in={showJobOffersDetails} timeout="auto" unmountOnExit>
                  <Box sx={{ fontSize: 12.5, lineHeight: 1.5, color: 'text.primary', mt: 1 }}>
                    Wyrażam zgodę na otrzymywanie przez okres trzech lat informacji dotyczących propozycji zatrudnienia w ramach rekrutacji prowadzonej przez UNITRESCO sp. z o.o. oraz na przechowywanie moich danych osobowych (w tym CV) w bazie danych UNITRESCO sp. z o.o. przez okres 3 lat w tym celu.<br/>
                    Zostałem/am poinformowany/a, że:
                    <ul style={{ marginTop: 6, marginBottom: 0 }}>
                      <li>Administratorem moich danych osobowych jest UNITRESCO sp. z o.o. z siedzibą przy ul. Żurawia 71, 15-540 Białystok</li>
                      <li>Moje dane będą przetwarzane wyłącznie w celu informowania o przyszłych możliwościach zatrudnienia</li>
                      <li>Podanie danych jest dobrowolne, ale niezbędne do otrzymywania informacji o ofertach pracy</li>
                      <li>Przysługuje mi prawo dostępu, sprostowania, usunięcia, ograniczenia przetwarzania i przenoszenia danych</li>
                      <li>Mogę w dowolnym momencie wycofać zgodę bez wpływu na zgodność z prawem przetwarzania przed jej wycofaniem — wycofanie zgody mogę zgłosić na adres: iod@unitresco.com</li>
                      <li>Po wycofaniu zgody moje dane zostaną usunięte z bazy kandydatów</li>
                      <li>Po upływie 3 lat od złożenia aplikacji moje dane zostaną automatycznie usunięte</li>
                      <li>Przysługuje mi prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych</li>
                      <li>Moje dane nie będą udostępniane podmiotom trzecim, z wyjątkiem podmiotów upoważnionych przepisami prawa</li>
                      <li>W sprawach związanych z ochroną danych mogą kontaktować się pod adresem: iod@unitresco.com</li>
                    </ul>
                  </Box>
                </Collapse>
              </Box>
            </Box>
            {siteKey ? (
              <Box sx={{ display:'flex', justifyContent:'center' }}>
                <ReCAPTCHA sitekey={siteKey} onChange={(v: string | null)=>setCaptchaToken(v)} />
              </Box>
            ) : (
              <Alert severity="error">Brak konfiguracji reCAPTCHA (SITE KEY). Skontaktuj się z administratorem.</Alert>
            )}
            {submitting && <LinearProgress />}
            <Button type="submit" variant="contained" disabled={submitting} fullWidth sx={{
              textTransform: 'none',
              fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '100%',
              borderRadius: '4px',
              padding: '12px 16px',
              color: 'rgba(255, 255, 255, 1)',
              backgroundColor: 'rgba(63, 63, 220, 1)',
              transition: 'background-color .15s ease',
              '&:hover': { backgroundColor: 'rgba(48, 48, 200, 1)' }
            }}>{submitting ? 'Wysyłanie…' : 'Aplikuj'}</Button>
          </Stack>
        </form>
        </Box>
      )}
    </Container>
  );
};

export default CareerApplyPage;


